import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as z from 'zod';
import type { DaemonRecord, DaemonUpdate } from '../../../generated/api/types.gen.js';
import { zDaemonUpdate } from '../../../generated/api/zod.gen.js';
import {
  Form,
  FormActions,
  FormFullWidth,
  FormSection,
  NumberFormField,
  RadioGroupFormField,
  SelectFormField,
  SwitchFormField,
  TextAreaFormField,
  TextFormField,
  useZodForm,
} from '../../../components/form/index.js';
import { useToast } from '../../../components/ui/index.js';
import { Can } from '../../../components/auth/Can.js';
import { useDaemonScope } from './DaemonScopeContext.js';
import { daemonKeys, daemonWrites } from './daemon-queries.js';

interface DaemonConfigTabProps {
  daemon: DaemonRecord;
}

const DEPARTMENTS = zDaemonUpdate.shape.department.unwrap().options;
const TIERS = zDaemonUpdate.shape.permissionTier.unwrap().options;
const DEPARTMENT_OPTIONS = DEPARTMENTS.map((value) => ({
  value,
  label: value.charAt(0).toUpperCase() + value.slice(1),
}));
const PERMISSION_TIERS = [
  { value: 'observer', label: 'Observer', description: 'Findings, docs, alerts' },
  { value: 'contributor', label: 'Contributor', description: 'Observer + PRs' },
  { value: 'operator', label: 'Operator', description: 'Contributor + external APIs' },
];

/** The generated update schema, with the channel list edited as one comma-separated text field. */
const configSchema = zDaemonUpdate
  .pick({ mandate: true, department: true, permissionTier: true, templateId: true, requiresApproval: true })
  .extend({ maxRunsPerDay: z.number().int().min(1), outputChannels: z.string() });

/** Edit a daemon's job description, output permissions and safety limits. */
export const DaemonConfigTab: React.FC<DaemonConfigTabProps> = ({ daemon }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { scope, policy, entityLabel } = useDaemonScope();
  const update = useMutation(daemonWrites(scope).update());

  const form = useZodForm(configSchema, {
    defaultValues: {
      mandate: daemon.mandate ?? '',
      department: DEPARTMENTS.find((d) => d === daemon.department) ?? 'custom',
      permissionTier: TIERS.find((t) => t === daemon.permissionTier) ?? 'observer',
      templateId: daemon.templateId ?? null,
      requiresApproval: daemon.requiresApproval ?? true,
      maxRunsPerDay: daemon.maxRunsPerDay ?? 10,
      // `outputChannels` is an open field in the contract, so it is narrowed to the string list this form edits.
      outputChannels: (Array.isArray(daemon.outputChannels)
        ? daemon.outputChannels.map(String)
        : ['findings', 'alerts']
      ).join(', '),
    },
  });

  const save = async (values: z.output<typeof configSchema>) => {
    const body: DaemonUpdate = {
      mandate: values.mandate || undefined,
      department: values.department,
      permissionTier: values.permissionTier,
      templateId: values.templateId ?? undefined,
      requiresApproval: values.requiresApproval,
      maxRunsPerDay: values.maxRunsPerDay,
      outputChannels: values.outputChannels
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    };
    await update.mutateAsync({ path: { id: String(daemon.id) }, body });
    await queryClient.invalidateQueries({ queryKey: daemonKeys(scope).get(daemon.id) });
    notify('Configuration saved');
  };

  return (
    <Form form={form} onSubmit={save} guardUnsaved className="space-y-8">
      <FormSection title="Job description">
        <FormFullWidth>
          <TextAreaFormField
            name="mandate"
            label="Mandate"
            rows={3}
            placeholder={`What is this ${entityLabel}'s mission? Be specific about goals and expected outputs.`}
          />
        </FormFullWidth>
        <SelectFormField name="department" label="Department" options={DEPARTMENT_OPTIONS} required />
        <NumberFormField
          name="templateId"
          label="Template ID"
          placeholder="e.g. 123456789"
          emptyAs={null}
          formatOptions={{ useGrouping: false }}
        />
      </FormSection>

      <FormSection title="Output permissions">
        <FormFullWidth>
          <RadioGroupFormField
            name="permissionTier"
            label="Permission tier"
            options={PERMISSION_TIERS}
            orientation="horizontal"
          />
        </FormFullWidth>
        <FormFullWidth>
          <TextFormField
            name="outputChannels"
            label="Output channels (comma-separated)"
            placeholder="findings, alerts, markdown_files"
          />
        </FormFullWidth>
      </FormSection>

      <FormSection title="Safety">
        <NumberFormField name="maxRunsPerDay" label="Max runs/day" minValue={1} />
        <SwitchFormField name="requiresApproval" label="Requires human approval for runs" />
      </FormSection>

      <Can policies={[policy]}>
        <FormActions submitLabel="Save configuration" />
      </Can>
    </Form>
  );
};
